import "dayjs/locale/pl"

import { ReactComponent as DeligooIconsSprite } from "@deligoo/ui/src/icons/build/deligoo-icons.svg"
import { configureStore } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import isToday from "dayjs/plugin/isToday"
import relativeTime from "dayjs/plugin/relativeTime"
import { createContext, useRef } from "react"
import type { Map } from "react-leaflet"
import { Provider, TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"
import { BrowserRouter, Route, Switch } from "react-router-dom"
import { AnyAction, Middleware as ReduxMiddleware } from "redux"

import { AppLayout } from "@/components/AppLayout"
import { PrivateRoute } from "@/components/PrivateRoute"
import { ToastContainer } from "@/components/Toast"
import { GlobalState } from "@/containers/GlobalState"
import { sentryReduxEnhancer } from "@/sentry"
import { rootReducer, RootState } from "@/store"
import { ClientsListView } from "@/views/ClientsListView"
import { DriversListView } from "@/views/DriversListView"
import { EventsList, EventsStatistics, EventsTiles } from "@/views/EventsView"
import { MapView } from "@/views/MapView"
import { OrdersListView } from "@/views/OrdersListView"
import { SignInView } from "@/views/SignInView"

import { useResaveUserSettingsToSession } from "./utils"
import { AddManualOrderView } from "./views/AddManualOrderView"
import { PaymentsView } from "./views/PaymentsView"

dayjs.extend(relativeTime)
dayjs.extend(isToday)
dayjs.extend(customParseFormat)
dayjs.locale("pl")

export const userTimingMiddleware: ReduxMiddleware = () => {
  return (next) => {
    return <A extends AnyAction>(action: A): A => {
      if (performance.mark === undefined) {
        return next(action)
      }

      performance.mark(`${action.type}_start`)
      const result = next(action)
      performance.mark(`${action.type}_end`)

      performance.measure(`${action.type}`, `${action.type}_start`, `${action.type}_end`)

      return result
    }
  }
}

export const globalStore = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(userTimingMiddleware),
  enhancers: (defaultEnhancers) => [...defaultEnhancers, sentryReduxEnhancer],
})

export type AppDispatch = typeof globalStore.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const MapContext = createContext<React.MutableRefObject<Map | null> | null>(null)

export const App = () => {
  const mapRef = useRef<Map | null>(null)

  useResaveUserSettingsToSession()

  // TODO: Handle redirect on 404

  return (
    <div className="App">
      <Provider store={globalStore}>
        <BrowserRouter>
          <GlobalState>
            <Switch>
              <Route exact path="/zaloguj" component={() => <SignInView />} />

              <MapContext.Provider value={mapRef}>
                <AppLayout>
                  <PrivateRoute path="/" component={() => <MapView />} exact />
                  <PrivateRoute path="/lista" component={() => <OrdersListView />} exact />
                  <PrivateRoute path="/lokalizacje*" component={() => <ClientsListView />} exact />
                  <PrivateRoute path="/kurierzy" component={() => <DriversListView />} exact />
                  <PrivateRoute path="/platnosci" component={() => <PaymentsView />} exact />
                  <PrivateRoute path="/centrum-zdarzen" component={() => <EventsTiles />} exact />
                  <PrivateRoute path="/centrum-zdarzen/statystyki" component={() => <EventsStatistics />} exact />
                  <PrivateRoute path="/centrum-zdarzen/lista" component={() => <EventsList />} exact />
                  <PrivateRoute path="/nowe-zlecenie-manualne" component={() => <AddManualOrderView />} exact />
                </AppLayout>
              </MapContext.Provider>
            </Switch>

            <ToastContainer position="top-center" />
          </GlobalState>
        </BrowserRouter>
      </Provider>

      <DeligooIconsSprite style={{ display: "none" }} />
    </div>
  )
}
